import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@mui/material';
import { IVideo } from 'types/auth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StepModify from './otherComponent/StepModify';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';

type VideoFormProps = {
  selectedNodeData: IVideo;
  selectedNode: any;
  onSubmit: (data: IVideo) => void;
  onClose: () => void;
  onStepTypeChange?: (newType: string) => void;
};

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type NotificationPayload = {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
};

const VideoForm = ({ selectedNodeData, onSubmit, onClose, selectedNode, onStepTypeChange }: VideoFormProps) => {
  const [initialValues, setInitialValues] = useState<IVideo | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: []
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  const clientObject = selectedNode?.data?.client || selectedNode?.data?.step?.client;
  const clientUid = clientObject?.uid || clientObject;
  console.log('🎬 VideoForm - clientObject:', clientObject);
  console.log('🎬 VideoForm - clientUid:', clientUid);

  // Memoize the callback to prevent re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    console.log('📋 Statuses loaded:', list.length);
    setStatuses(list);
  }, []);

  // Memoize notification change handler to prevent re-renders
  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  // Memoize escalation change handler to prevent re-renders
  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 VideoForm: Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []);

  // Memoize step type change handler
  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    console.log('🔄 Step type changing to:', newType);
    const type = newType.toUpperCase();
    setFieldValue('stepType', type);
    setFieldValue('componentType', type);
    setFieldValue('data.title', type);
    if (onStepTypeChange) onStepTypeChange(type);
    setTimeout(() => {
      submitForm();
    });
  }, [onStepTypeChange]);

  useEffect(() => {
    const {
      componentType,
      name = selectedNodeData.component?.name || '',
      description,
      settings,
      minimumAnswers,
      formType,
      component = {}
    } = selectedNodeData;
    const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

    // Extract and map notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    const mappedNotification = {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };

    setInitialValues({
      componentType,
      name,
      description,
      settings,
      minimumAnswers,
      formType,
      component,
      data: {
        title: selectedNode?.data?.label
      },
      actor: actor,
      stepType: selectedNode?.step?.stepType || 'VIDEO',
      onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete: selectedNode?.data?.step?.onComplete ? selectedNode?.data?.step?.onComplete : {}
    });

    // Set notification payload separately to avoid type issues
    setNotificationPayload({
      conditions: [
        ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
        ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
      ],
      users: mappedNotification.notifyUsers || [],
      roles: mappedNotification.notifyRoles || [],
      channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
        ? mappedNotification.notificationChannel.map((channel: string) => 
            channel === 'In-App' ? 'IN-APP' : 
            channel === 'Email' ? 'EMAIL' :
            channel === 'SMS' ? 'SMS' : channel.toUpperCase()
          )
        : []
    });

    // Initialize escalation payload from existing node data
    setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.
  }, [selectedNodeData.componentType, selectedNodeData.name, selectedNodeData.description, selectedNodeData.settings, selectedNode?.data?.label, selectedNode?.data?.step?.actor, selectedNode?.data?.step?.notification, selectedNode?.data?.step?.escalation, selectedNode?.data?.step?.onComplete?.status?.uid]);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  return (
    initialValues && (
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
       onSubmit={(values, { setSubmitting }) => {
          const selectedStatus = statuses.find(s => s.uid === values.onCompleteStatusUid);

          const updatedData = {
            ...values,
            component: {
              ...values.component,
              name: values.name,
              componentType: values.componentType || 'VIDEO'
            },
            onComplete: selectedStatus ? { status: selectedStatus } : {},
            notification: notificationPayload,
            escalation: escalationPayload
          };

          console.log("📦 Final Payload Sent:", updatedData);
          onSubmit(updatedData);
          setSubmitting(false);
        }}

      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitForm }) => {
           
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Paper sx={{ p: 4, width: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h4" fontWeight="bold">
                    {values.data.title || values.stepType}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isEditingTitle ? (
                      <>
                        <TextField
                          name="data.title"
                          value={values.data.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoFocus
                          size="small"
                        />
                        <Button variant="contained" size="small" sx={{ ml: 1 }} onClick={() => setIsEditingTitle(false)}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outlined" size="small" sx={{ ml: 1 }} onClick={() => setIsEditingTitle(true)}>
                          Edit
                        </Button>
                        {selectedNode && (
                          <StepModify
                            selectedNode={selectedNode}
                            onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography variant="h6" mb={2}>Basic Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid container alignItems="center">
                        <Grid item xs={4}><Typography>Title:</Typography></Grid>
                        <Grid item xs={8}>
                          <TextField size="small" name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} fullWidth />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container alignItems="center">
                        <Grid item xs={4}><Typography>Description:</Typography></Grid>
                        <Grid item xs={8}>
                          <TextField
                            size="small"
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            multiline
                            minRows={3}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Typography variant="h6" mt={4} mb={2}>Settings</Typography>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Fields Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box border={1} borderColor="red.300" borderRadius={2} p={2}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}><Typography>Max Size:</Typography></Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              name="settings.maxSize"
                              type="number"
                              value={values.settings.maxSize || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}><Typography>Min Duration:</Typography></Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              name="settings.minDuration"
                              type="number"
                              value={values.settings.minDuration || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}><Typography>Max Duration:</Typography></Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              name="settings.maxDuration"
                              type="number"
                              value={values.settings.maxDuration || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}><Typography>Watermark:</Typography></Grid>
                          <Grid item xs={6}>
                            <Checkbox
                              name="settings.watermark"
                              checked={values.settings.watermark || false}
                              onChange={(e) => {
                                setFieldValue('settings.watermark', e.target.checked);
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}><Typography>Coordinate:</Typography></Grid>
                          <Grid item xs={6}>
                            <Checkbox
                              name="settings.coordinate"
                              checked={values.settings.coordinate || false}
                              onChange={(e) => {
                                setFieldValue('settings.coordinate', e.target.checked);
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}><Typography>Camera Mode:</Typography></Grid>
                          <Grid item xs={6}>
                            <Select
                              name="settings.cameraMode"
                              size="small"
                              value={values.settings.cameraMode || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                            >
                              <MenuItem value="environment">Environment</MenuItem>
                              <MenuItem value="front">Front</MenuItem>
                            </Select>
                          </Grid>
                        </Grid>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  
                  <CommonNodeSetting
                    selectedNode={selectedNode}
                    onStatusesLoaded={handleStatusesLoaded}
                    onNotificationChange={handleNotificationChange}
                    notificationInitialValues={notificationInitialValues}
                    escalationPayload={escalationPayload}
                    onEscalationChange={handleEscalationChange}
                  />

                  <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                    <Button type="button" color="error" sx={{ py: 1, fontSize: '1rem' }} onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary" sx={{ py: 1, fontSize: '1rem' }}>
                      Save
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          );
        }}
      </Formik>
    )
  );
};

export default memo(VideoForm);
